import { Component, Element, h, Listen, Prop, Watch, Host, JSX } from '@stencil/core';
import { ActionTrackingEvent, VirtualPageTrackingEvent } from '@union-investment/tracking/src/events';
import { isMagnoliaEditingMode } from '../../../helper';

export interface UiCmsAccordionBody {
  summary: string;
  open?: boolean;
  anchorName?: string;
}

@Component({
  tag: 'ui-cms-accordion',
  styleUrl: 'ui-cms-accordion.scss',
})
export class UiCmsAccordion {
  @Element() el: HTMLUiCmsAccordionElement;

  @Prop({ mutable: true }) body: UiCmsAccordionBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  @Listen('hashchange', { target: 'window' })
  onHashChange(e: HashChangeEvent) {
    this.openAccordionIfHashMatchesAnchor(new URL(e.newURL).hash);
  }

  private refAnchor: HTMLUiCmsAnchorElement;

  private refAccordion: any;

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
    if (isMagnoliaEditingMode()) this.body.open = true;
  }

  componentDidLoad() {
    this.openAccordionIfHashMatchesAnchor(window.location.hash);
  }

  private openAccordionIfHashMatchesAnchor(hash: string): void {
    if (hash) {
      const anchorElems = Array.from(this.el.querySelectorAll('ui-cms-anchor'));
      if (this.refAnchor) anchorElems.push(this.refAnchor);
      const matchingAnchor = anchorElems.find((anchor) => {
        const anchorId = anchor.getAttribute('id');
        if (anchorId) {
          return anchorId === hash.replace('#', '');
        }
        return false;
      });
      if (matchingAnchor) {
        this.refAccordion.show();
        this.track();
      }
    }
  }

  private track(): void {
    const eventName = `${this.body.open ? 'Close' : 'Open'} Accordion`;

    const actionTrackingEvent: ActionTrackingEvent = {
      eventInfo: {
        eventName,
        eventType: 'Action',
      },
      attributes: {
        elementType: 'Button',
        elementLabel: this.body.summary,
        virtualTarget: this.body.summary,
      },
      category: {
        area: 'Content',
      },
    };

    const virtualPageTrackingEvent: VirtualPageTrackingEvent = {
      eventInfo: {
        eventName,
        eventType: 'VirtualPage',
      },
      attributes: {
        virtualContentGroups: [this.body.summary],
      },
    };

    if (actionTrackingEvent) {
      window.ui?.services?.tracking?.track(actionTrackingEvent);
    }

    if (virtualPageTrackingEvent) {
      window.ui?.services?.tracking?.track(virtualPageTrackingEvent);
    }
  }

  private renderAnchor(): JSX.Element {
    return (
      <ui-cms-anchor
        ref={(el) => {
          this.refAnchor = el;
        }}
        id={this.body.anchorName}
      ></ui-cms-anchor>
    );
  }

  render() {
    return (
      <Host>
        {this.body.anchorName && this.renderAnchor()}
        <sd-accordion
          ref={(el) => {
            this.refAccordion = el;
          }}
          onClick={() => this.track()}
          summary={this.body.summary}
          open={this.body.open}
        >
          <slot></slot>
        </sd-accordion>
      </Host>
    );
  }
}
